/**
 * Hero Mobile Switch
 * 
 * Mobile-only: Switches full hero (gallery + content) between v1 and v2.
 * 
 * After experiment concludes:
 * 1. Update ../index.tsx to export winner directly
 * 2. Delete this variants/ folder
 */

import { ExperimentalSection } from 'ui/experimental-section';
import { AllowedExperimentKey } from '@tenants/types';
import SwiperCarousel from 'components/swiper-carousel';
import { ListingGallerySection, GALLERY_VARIANTS } from '../../gallery';
import { HeroContentV1 } from './hero-content.v1';
import { HeroContentV2Mobile } from './hero-content.v2';
import { useExperimentVariation } from 'ui/experimental-section/hooks';
import type { HeroContentSwitchProps } from './hero-content.switch';
import type { GallerySectionData } from '../../gallery/types';
import type { ComplianceData } from 'ui/compliance';

export interface HeroMobileSwitchProps extends HeroContentSwitchProps {
  gallery: GallerySectionData;
  complianceData?: ComplianceData;
  onImageClick?: (index: number) => void;
  onSlideChange?: (index: number) => void;
  onComplianceClick?: () => void;
  share?: {
    text: string;
    url: string;
    listingId: number;
  };
  priceHistoryRef?: React.RefObject<HTMLDivElement>;
}

export function HeroMobileSwitch({
  // Content props
  listing,
  currency,
  locale,
  blur,
  mlsCourtesyText,
  mlsBuyerRepText,
  virtualTourLabel,
  onVirtualTourClick,
  priceHistoryLabel,
  onPriceHistoryClick,
  onAddressClick,
  listingSource,
  tenantId,
  complianceRequirement,
  shouldScramble,
  // Gallery props
  gallery,
  complianceData,
  onImageClick,
  onSlideChange,
  onComplianceClick,
  share,
  priceHistoryRef,
}: HeroMobileSwitchProps) {
  const complianceOverlay = complianceData?.shouldShow
    ? { message: complianceData.message, ctaText: complianceData.ctaText, onClick: onComplianceClick }
    : undefined;

  const mapPosition = listing.displayAddress === false ? null : listing.position;

  // Check if LISTING_HERO variation is active - if so, hide share button in gallery
  // since it's shown in the sticky footer instead
  const { showVariation: isListingHeroActive } = useExperimentVariation(AllowedExperimentKey.LISTING_HERO);

  return (
    <ExperimentalSection
      experimentKey={AllowedExperimentKey.LISTING_HERO}
      control={
        <>
          <SwiperCarousel
            listingData={{
              id: listing.id,
              imageUrls: listing.imageUrls,
              imageDescs: listing.imageDescs,
              position: mapPosition,
              onImageClick: onImageClick as ((index: number | 'map') => void) | undefined,
            }}
            imageAltFallback="Listing image"
            complianceRequirement={complianceRequirement}
            onComplianceRequirementClick={onComplianceClick ? () => onComplianceClick() : undefined}
            share={share}
            showFavouriteToggle={false}
          />
          <div style={{ marginTop: '-1em', padding: '16px 20px 0' }}>
            <HeroContentV1
              tenantId={tenantId}
              listing={listing}
              complianceRequirement={complianceRequirement}
              shouldScramble={shouldScramble}
              priceHistoryLabel={priceHistoryLabel}
              onPriceHistoryClick={onPriceHistoryClick}
              priceHistoryRef={priceHistoryRef}
              listingSource={listingSource}
            />
          </div>
        </>
      }
      variation={
        <>
          <ListingGallerySection
            sectionId="hero-gallery"
            data={gallery}
            variant={GALLERY_VARIANTS.CAROUSEL}
            onImageClick={onImageClick}
            onSlideChange={onSlideChange}
            blur={blur}
            complianceOverlay={complianceOverlay}
            share={isListingHeroActive ? undefined : share}
          />
          <HeroContentV2Mobile
            listing={listing}
            currency={currency}
            locale={locale}
            blur={blur}
            mlsCourtesyText={mlsCourtesyText}
            mlsBuyerRepText={mlsBuyerRepText}
            virtualTourLabel={virtualTourLabel}
            onVirtualTourClick={onVirtualTourClick}
            priceHistoryLabel={priceHistoryLabel}
            onPriceHistoryClick={onPriceHistoryClick}
            onAddressClick={onAddressClick}
            listingSource={listingSource}
          />
        </>
      }
    />
  );
}
